import { defineAsyncComponent } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';

import UserAuth from './pages/auth/UserAuth.vue';
import ClientAuth from './pages/auth/ClientAuth.vue';
import Pay from './pages/pay/index.vue';
// Experimental /pay-deep sandbox — byte-for-byte copy of Pay used as an
// R&D surface. Points at pages/pay-deep/index.vue so nothing here can
// touch the production /pay path.
import PayDeep from './pages/pay-deep/index.vue';
import InstantWithdraw from './pages/instant-withdraw/index.vue';
import WithdrawalSuccess from './pages/instant-withdraw/success.vue';
import CreatePayIn from './pages/manual-order/create-payin.vue';
import CreatePayOut from './pages/manual-order/create-payout.vue';
import Deposit from './pages/deposit/index.vue';
import Success from './pages/manual-order/success.vue';
import Pending from './pages/manual-order/pending.vue';
import Failed from './pages/manual-order/failed.vue';
import NotFound from './pages/NotFound.vue';
import NoPermission from './pages/NoPermission.vue';
import store from './store/index.js';

const Dashboard = defineAsyncComponent(() =>
  import('./pages/dashboard/index.vue')
);

const PayIn = defineAsyncComponent(() => import('./pages/payin/index.vue'));
const CreatePayInClient = defineAsyncComponent(() => import('./pages/payin/CreatePayInClient.vue'));
const SettlementReconciliation = defineAsyncComponent(() =>
  import('./pages/payin/SettlementReconciliation.vue')
);
const BankStatementVerification = defineAsyncComponent(() =>
  import('./pages/payin/BankStatementVerification.vue')
);
const Callback = defineAsyncComponent(() =>
  import('./pages/callback/index.vue')
);
const CallbackLogs = defineAsyncComponent(() =>
  import('./pages/callback-logs/index.vue')
);
const PayOut = defineAsyncComponent(() => import('./pages/payout/index.vue'));
const CreatePayOutClient = defineAsyncComponent(() => import('./pages/payout/CreatePayOutClient.vue'));
// Instant Pay Out + Instant Payout Market: feature deprecated, operator UI
// removed. Routes are no longer registered. The /instant-withdraw/:id
// customer-facing page is kept so any legacy links still resolve.
const Users = defineAsyncComponent(() => import('./pages/users/index.vue'));
const SubAdmin = defineAsyncComponent(() =>
  import('./pages/sub-admin/index.vue')
);
const Extensions = defineAsyncComponent(() =>
  import('./pages/extensions/index.vue')
);
const Clients = defineAsyncComponent(() => import('./pages/clients/index.vue'));
const Routing = defineAsyncComponent(() => import('./pages/routing/index.vue'));
const Reports = defineAsyncComponent(() => import('./pages/reports/index.vue'));
const AdminReport = defineAsyncComponent(() => import('./pages/admin-report/index.vue'));
const ClientPartnerReport = defineAsyncComponent(() => import('./pages/client-partner-report/index.vue'));
const Vendor = defineAsyncComponent(() => import('./pages/vendor/index.vue'));
const ClientPartners = defineAsyncComponent(() => import('./pages/client-partners/index.vue'));
const SupportUser = defineAsyncComponent(() =>
  import('./pages/support-user/index.vue')
);
const OrderCreator = defineAsyncComponent(() =>
  import('./pages/order-creator/index.vue')
);
const ManualOrder = defineAsyncComponent(() =>
  import('./pages/manual-order/index.vue')
);
const UploadStatement = defineAsyncComponent(() =>
  import('./pages/upload-statement/index.vue')
);
const UploadHistory = defineAsyncComponent(() =>
  import('./pages/upload-history/index.vue')
);
const ResetPassword = defineAsyncComponent(() =>
  import('./pages/reset-password/index.vue')
);
const ChangePassword = defineAsyncComponent(() =>
  import('./pages/change-password/index.vue')
);
const Security = defineAsyncComponent(() =>
  import('./pages/account/Security.vue')
);
const Ledger = defineAsyncComponent(() =>
  import('./pages/ledger/index.vue')
);
const AccountHub = defineAsyncComponent(() =>
  import('./pages/account/index.vue')
);
const SupportedUsers = defineAsyncComponent(() =>
  import('./pages/account/SupportedUsers.vue')
);

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      component: UserAuth,
      meta: {
        requiresUnauth: true,
        layout: 'blank'
      }
    },
    {
      path: '/login',
      component: UserAuth,
      meta: {
        requiresUnauth: true,
        layout: 'blank'
      }
    },
    {
      path: '/client-login',
      component: ClientAuth,
      meta: {
        requiresUnauth: true,
        layout: 'blank'
      }
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: Dashboard,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/payin',
      name: 'Pay In',
      component: PayIn,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/payin/create',
      name: 'Create Pay In Client',
      component: CreatePayInClient,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/payin/verify-statement',
      name: 'Bank Statement Verification',
      component: BankStatementVerification,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/payin/reconcile',
      name: 'Settlement Reconciliation',
      component: SettlementReconciliation,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/callback',
      name: 'Callback',
      component: Callback,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/callback-logs',
      name: 'Callback Logs',
      component: CallbackLogs,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/payout',
      name: 'Pay Out',
      component: PayOut,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/payout/create',
      name: 'Create Pay Out Client',
      component: CreatePayOutClient,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },

    {
      path: '/manual-order',
      name: 'Manual Orders',
      component: ManualOrder,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/banks',
      name: 'Banks',
      component: Users,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/branches',
      name: 'Branches',
      component: SubAdmin,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/extensions',
      name: 'Extensions',
      component: Extensions,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/vendor',
      name: 'Vendors',
      component: Vendor,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      // Client Partners management — superadmin only (guard: isSuperAdmin
      // allows all; other roles can't reach it — not in any whitelist/allowed
      // set, and the backend routes are isSuperAdmin-gated).
      path: '/client-partners',
      name: 'Client Partners',
      component: ClientPartners,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/order-creator',
      name: 'Order Creators',
      component: OrderCreator,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/support-user',
      name: 'Support Users',
      component: SupportUser,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },

    {
      path: '/instant-withdraw/:id',
      name: 'Withdraw',
      component: InstantWithdraw,
      meta: {
        layout: 'blank'
      },
      props: route => ({ id: route.params.id || '' })
    },
    {
      path: '/instant-withdraw/success',
      name: 'Withdrawal Success',
      component: WithdrawalSuccess,
      meta: {
        layout: 'blank'
      }
    },
    {
      path: '/pay/:id',
      name: 'Pay',
      component: Pay,
      meta: {
        layout: 'blank',
        referrer: 'no-referrer'
      },
      props: route => ({ id: route.params.id || '' })
    },
    // Experimental sandbox route — same behaviour as /pay/:id but backed
    // by pages/pay-deep/index.vue so changes here can't affect the
    // production customer payment flow.
    {
      path: '/pay-deep/:id',
      name: 'PayDeep',
      component: PayDeep,
      meta: {
        layout: 'blank',
        referrer: 'no-referrer'
      },
      props: route => ({ id: route.params.id || '' })
    },
    {
      path: '/create-payin/:refID',
      name: 'Create Pay In',
      component: CreatePayIn,
      meta: {
        layout: 'blank'
      },
      props: route => ({ refID: route.params.refID || '' })
    },
    {
      path: '/create-payout/:refID',
      name: 'Create Pay Out',
      component: CreatePayOut,
      meta: {
        layout: 'blank'
      },
      props: route => ({ refID: route.params.refID || '' })
    },
    {
      // New form: /deposit/<order_creator>/<clientName>. The clientName flows
      // through to the backend so the order is correctly tagged on multi-client
      // vendors. The legacy form below (no clientName) still works and falls
      // back to the vendor's first secrets row.
      path: '/deposit/:vendor/:clientName',
      name: 'Deposit With Client',
      component: Deposit,
      meta: {
        layout: 'blank'
      },
      props: route => ({
        vendor: route.params.vendor || '',
        clientName: route.params.clientName || '',
      })
    },
    {
      path: '/deposit/:vendor',
      name: 'Deposit',
      component: Deposit,
      meta: {
        layout: 'blank'
      },
      props: route => ({ vendor: route.params.vendor || '', clientName: '' })
    },
    {
      path: '/success',
      name: 'Success',
      component: Success,
      meta: {
        layout: 'blank'
      }
    },
    {
      path: '/pending',
      name: 'Pending',
      component: Pending,
      meta: {
        layout: 'blank'
      }
    },
    {
      path: '/failed',
      name: 'Failed',
      component: Failed,
      meta: {
        layout: 'blank'
      }
    },
    {
      path: '/clients',
      name: 'Clients',
      component: Clients,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    // Client → branch routing groups (superadmin only). Route guard is
    // via the adminRestrictedPath list — /routing is included there so
    // the beforeEach guard sends non-superadmin roles to /no-permission.
    {
      path: '/routing',
      name: 'Routing',
      component: Routing,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/reports',
      name: 'Reports',
      component: Reports,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/admin-report',
      name: 'Admin Report',
      component: AdminReport,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      // Client Partner report — client_partner only (in clientPartnerAllowedPaths).
      path: '/client-partner-report',
      name: 'My Clients Report',
      component: ClientPartnerReport,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/upload-statement',
      name: 'Upload Statement',
      component: UploadStatement,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/upload-history',
      name: 'Upload History',
      component: UploadHistory,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    {
      path: '/reset-password',
      name: 'Reset Password',
      component: ResetPassword,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      }
    },
    // Account hub — Security (2FA) + Change Password + Ledger under a single
    // shell with left sub-nav. Existing standalone URLs (/change-password,
    // /ledger) redirect here so bookmarks + in-app links keep working. The
    // old /account/security URL is preserved 1:1 as the security child.
    {
      path: '/account',
      component: AccountHub,
      meta: {
        requiresAuth: true,
        layout: 'dashboard'
      },
      children: [
        { path: '', redirect: '/account/security' },
        {
          path: 'security',
          name: 'Security',
          component: Security,
        },
        {
          path: 'password',
          name: 'Account Password',
          component: ChangePassword,
        },
        {
          path: 'ledger',
          name: 'Account Ledger',
          component: Ledger,
        },
        {
          path: 'supported-users',
          name: 'Supported Users',
          component: SupportedUsers,
        },
      ],
    },
    // Backward-compat redirects for previously top-level pages. Guards on
    // the target path still apply (e.g., /account/ledger inherits ledger
    // access rules from the restricted-paths lists below).
    {
      path: '/change-password',
      redirect: '/account/password',
    },
    {
      path: '/ledger',
      redirect: '/account/ledger',
    },
    {
      path: '/:notFound(.*)',
      component: NotFound,
      meta: {
        layout: 'blank'
      }
    },
    {
      path: '/no-permission',
      component: NoPermission,
      meta: { layout: 'dashboard', requiresAuth: true }
    }
  ]
});

// /clients is admin-restricted ONLY when the vendor doesn't have the
// `canManageClients` grant. The beforeEach guard below resolves that at
// navigation time using the auth state.
// `/routing` is superadmin-only. Admin (vendor), subadmin, user, support
// and order-creator all get blocked here — the superadmin gate lives on
// the backend too (isSuperAdmin middleware on every /api/v1/routing/*
// endpoint), the frontend restriction just avoids showing the empty page.
const adminRestrictedPath = ['/vendor', '/reset-password', '/routing'];
// Paths a vendor (role='admin') can reach ONLY when superadmin has set
// users.can_manage_clients = 1. Both /clients and /admin-report share
// this same flag — ops asked for a single grant to unlock all vendor
// self-serve pages rather than a toggle per page. Values are auth
// getter names (the getter resolved dynamically in the beforeEach guard).
const adminConditionalPath = {
  '/clients': 'canManageClients',
  '/admin-report': 'canManageClients',
  // Ledger visibility to vendor (role='admin') gated on the same
  // superadmin-managed grant. Superadmin bypasses via isSuperAdmin branch;
  // subadmin/client/user/etc. are already blocked via their own lists.
  '/ledger': 'canManageClients',
  '/account/ledger': 'canManageClients',
};
const userRestrictedPath = [
  '/banks',
  '/clients',
  'reports',
  '/upload-statement',
  '/reset-password',
  '/callback-logs',
  '/admin-report',
  '/routing',
  '/ledger',
  '/account/ledger'
];
const subAdminrestrictedPath = [
  // '/banks',
  '/clients',
  'reports',
  '/support-user',
  '/vendor',
  'order-creator',
  '/upload-statement',
  '/admin-report',
  '/routing',
  // Ledger self-view is now allowed for branches — the tab inside
  // AccountHub gates on the presence of an opening balance for the
  // signed-in branch. If ops hasn't posted one, the tab is hidden and
  // deep-linking to /account/ledger still loads the empty-state page.
  // (Removed: '/ledger', '/account/ledger')
];
const userPermissionPath = ['/payin', '/payout', '/reset-password'];
const supportrestrictedPath = [
  '/banks',
  '/clients',
  'reports',
  '/support-user',
  '/vendor',
  '/payin',
  '/payout',
  '/order-creator',
  '/upload-statement',
  '/reset-password',
  '/callback-logs',
  '/admin-report',
  '/routing',
  '/ledger',
  '/account/ledger'
];
const orderCreatorRestrictedPath = [
  '/banks',
  '/clients',
  'reports',
  '/support-user',
  '/vendor',
  '/payin',
  '/payout',
  '/order-creator',
  '/upload-statement',
  '/reset-password',
  '/callback-logs',
  '/admin-report',
  '/routing',
  '/ledger',
  '/account/ledger'
];

// Supporting-user client-side gating (Phase 1). Mirrors the backend
// deny-by-default routeMap: a supporting user may reach a route only if it is
// a self/account route OR it maps to a permission they actually hold. Any
// other route redirects to their first allowed landing. The backend remains
// the real authority — this only stops them from landing on a page that would
// 403. Add entries as more endpoints get mapped server-side.
const supportingRoutePermission = {
  '/dashboard': 'dashboard.view',
  '/payin': 'payin.list.view',
  '/payout': 'payout.list.view',
  '/banks': 'bank.list.view',
  '/branches': 'branch.list.view',
  '/clients': 'client.list.view',
  '/reports': 'report.view',
  '/admin-report': 'report.admin.view',
  '/callback-logs': 'callback.logs.view',
  '/upload-history': 'payin.statement.view',
  '/payin/reconcile': 'settlement.view',
  '/ledger': 'ledger.view',
};

// First route a supporting user is allowed to see (used as their landing and
// as the redirect target when they hit something they can't access). Falls
// back to account security, which is always reachable (self-service).
function firstSupportingPath() {
  const order = ['/dashboard', '/payin', '/payout', '/banks'];
  for (const p of order) {
    if (store.getters.hasPermission(supportingRoutePermission[p])) return p;
  }
  return '/account/security';
}

router.beforeEach(function(to, _, next) {
  // Geo gate runs in main.js BEFORE mount, so by the time this guard fires
  // we're already past the block check. Nothing to do here.
  if (to.meta.requiresAuth && !store.getters.isAuthenticated) {
    next('/');
  } else if (to.meta.requiresUnauth && store.getters.isAuthenticated) {
    next('/dashboard');
  } else if (store.getters.isSupporting) {
    // Supporting users are gated by EFFECTIVE PERMISSIONS, not role. Must be
    // intercepted BEFORE the role branches below (they inherit the parent
    // role, so isAdmin/isSubAdmin/etc. would otherwise wave them through).
    //
    // Consume the post-login flag ONCE per supporting navigation: the FIRST
    // redirect right after logging in is silent; any later navigation (direct
    // URL / deep link / in-app) to a page they can't access shows a notice.
    const path = to.path;
    const silent = store.getters.justLoggedIn;
    store.commit('setJustLoggedIn', false);
    if (path === '/account/supported-users') {
      // One-level rule (21092026): a supporting user can't manage their own
      // supporting users. Backend denies it too; keep them off the page.
      next('/account/security');
    } else if (path === '/no-permission' || path === '/account' || path.startsWith('/account/')) {
      // Self-service / account pages are always reachable.
      next();
    } else {
      const required = supportingRoutePermission[path];
      if (required && store.getters.hasPermission(required)) {
        next();
      } else {
        // Not allowed → send them to their best available page, with a notice
        // unless this is the silent first-login redirect.
        if (!silent) {
          store.commit('setAccessNotice', "You don't have permission to access that page.");
        }
        next(firstSupportingPath());
      }
    }
  } else {
    if (store.getters.isSuperAdmin) {
      next();
    } else if (store.getters.isClient) {
      // `/account/ledger` is now allowed for clients too — same gating
      // as branches: the tab inside AccountHub only appears when the
      // client has an opening balance posted; direct URL navigation
      // reaches the ledger page's own empty-state UI when there's none.
      const clientAllowedPaths = ['/payin', '/payout', '/payin/create', '/payout/create', '/dashboard', '/reports', '/account', '/account/security', '/account/password', '/account/ledger', '/account/supported-users'];
      if (clientAllowedPaths.includes(to.path)) {
        next();
      } else {
        next('/no-permission');
      }
    } else if (store.getters.isClientPartner) {
      // Client Partner (11102026): view-only. Whitelisted to the scoped
      // dashboard + pay-in/pay-out lists + own account security. Everything
      // else → no-permission. The backend scopes every one of these to the
      // partner's owned clients.
      const clientPartnerAllowedPaths = ['/dashboard', '/payin', '/payout', '/client-partner-report', '/account', '/account/security', '/account/password'];
      if (clientPartnerAllowedPaths.includes(to.path)) {
        next();
      } else {
        next('/no-permission');
      }
    } else if (store.getters.isAdmin) {
      // Dashboard is hidden for vendor (admin) — redirect to their first
      // operational page (covers direct URL + the post-login landing, which
      // defaults to /dashboard) — UNLESS the global toggle VENDOR_DASHBOARD_ENABLED
      // is on (JWT claim → localStorage).
      if (to.path === '/dashboard' && localStorage.getItem('vendorDashboardEnabled') !== '1') {
        next('/payin');
      } else if (adminRestrictedPath.includes(to.path)) {
        next('/no-permission');
      } else if (adminConditionalPath[to.path]) {
        // /clients (and any other conditional admin path) gated on the
        // matching auth getter. If the flag isn't set, route to
        // /no-permission instead of letting the page load and 403.
        const requiredGetter = adminConditionalPath[to.path];
        if (store.getters[requiredGetter]) {
          next();
        } else {
          next('/no-permission');
        }
      } else {
        next();
      }
    } else if (store.getters.isSubAdmin) {
      if (subAdminrestrictedPath.includes(to.path)) {
        next('/no-permission');
      } else {
        next();
      }
    } else if (store.getters.isOrderCreator) {
      if (orderCreatorRestrictedPath.includes(to.path)) {
        next('/no-permission');
      } else {
        next();
      }
    } else if (store.getters.isSupport) {
      if (supportrestrictedPath.includes(to.path)) {
        next('/no-permission');
      } else {
        next();
      }
    } else if (store.getters.isUser) {
      if (userRestrictedPath.includes(to.path)) {
        next('/no-permission');
      } else {
        if (userPermissionPath.includes(to.path)) {
          if (to.path == '/payin') {
            if (store.getters.payInStatus == '0') {
              next('/no-permission');
            } else {
              next();
            }
          } else if (to.path == '/payout') {
            if (store.getters.payOutStatus == '0') {
              next('/no-permission');
            } else {
              next();
            }
          } else {
            next();
          }
        } else {
          next();
        }
      }
    } else {
      next();
    }
  }
});

export default router;
 